import { DomainError } from '../errors.js';

/**
 * Tags: operator-defined labels for grouping sites.
 *
 * The colour is a palette key rather than a hex value. A tag chosen as
 * `#ffe066` in light mode is illegible in dark mode, and asking an operator to
 * pick two colours for one tag is asking them to do the theme's job.
 */

export const TAG_COLORS = [
  'slate',
  'blue',
  'teal',
  'green',
  'amber',
  'orange',
  'red',
  'pink',
  'violet',
  'indigo',
] as const;

export type TagColor = (typeof TAG_COLORS)[number];

export const DEFAULT_TAG_COLOR: TagColor = 'slate';

export function isTagColor(value: string): value is TagColor {
  return (TAG_COLORS as readonly string[]).includes(value);
}

export const TAG_NAME_MAX = 40;

/**
 * Normalises a tag name and refuses one that cannot be told apart from another.
 *
 * Collapsing inner whitespace matters: "Клиент  А" and "Клиент А" would
 * otherwise coexist as two tags nobody can distinguish in a filter bar.
 */
export function normaliseTagName(raw: string): string {
  const name = raw.trim().replace(/\s+/gu, ' ');

  if (name.length === 0) {
    throw new DomainError('VALIDATION_FAILED', 'A tag needs a name', {});
  }
  if (name.length > TAG_NAME_MAX) {
    throw new DomainError('VALIDATION_FAILED', `A tag name is at most ${TAG_NAME_MAX} characters`, {});
  }

  return name;
}

export function assertTagColor(value: string): asserts value is TagColor {
  if (!isTagColor(value)) {
    throw new DomainError('VALIDATION_FAILED', `Unknown tag colour: ${value}`, { color: value });
  }
}
